import {
  PIN_HEIGHT,
  PIN_WIDTH,
  PinInput,
  escapeHtml,
  luminance,
  readableOn,
  renderTitle,
  requireImageUrl,
  requireTitle,
} from "./types";

export function listPinTemplate(input: PinInput): string {
  const { eyebrow, titleAccent, brand, brandLogo, cta, palette, typography, items } = input;
  // The bottom bar uses palette.ink as its background. For dark palettes,
  // ink is light → bar is light → logo must be dark for contrast.
  const isDarkPalette = luminance(palette.bg) < luminance(palette.ink);
  const brandLogoFilter = isDarkPalette
    ? "brightness(0)"
    : "brightness(0) invert(1)";
  const title = requireTitle(input, "listPin");
  if (!items || items.length < 1) {
    throw new Error("listPin template requires items[]");
  }
  // Length→size ladder (same pattern as the venue templates) so long titles
  // shrink instead of silently truncating at the 2-line clamp.
  const titleLen = title.length;
  const titleSize = titleLen <= 32 ? 78 : titleLen <= 44 ? 68 : titleLen <= 58 ? 58 : 50;
  // Number circles put numText (or bg) on primary — guard against a client
  // brand where those end up light-on-light.
  const numInk = readableOn(palette.primary, palette.numText ?? palette.bg);

  const allHaveImages = items.every((it) => !!it.imageUrl);
  const hasThumbs = allHaveImages;

  const heroUrl = hasThumbs ? null : requireImageUrl(input, "listPin");

  const rows = items
    .map((it, i) => {
      const num = i + 1;
      // Captions may include a "Price: ..." line — render newlines as <br>
      const captionHtml = it.caption
        ? escapeHtml(it.caption).replace(/\n/g, "<br>")
        : "";
      if (hasThumbs) {
        return `
        <li class="row row-thumbs">
          <div class="thumb" style="background-image:url('${escapeHtml(it.imageUrl!)}')">
            <span class="thumb-num">${num}</span>
          </div>
          <div class="row-text">
            <div class="row-name">${escapeHtml(it.name)}</div>
            ${captionHtml ? `<div class="row-cap">${captionHtml}</div>` : ""}
          </div>
        </li>`;
      }
      return `
      <li class="row">
        <span class="num">${num}</span>
        <div class="row-text">
          <div class="row-name">${escapeHtml(it.name)}</div>
          ${captionHtml ? `<div class="row-cap">${captionHtml}</div>` : ""}
        </div>
      </li>`;
    })
    .join("");

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;background:${palette.bg};color:${palette.ink};font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased}
  .pin{width:100%;height:100%;display:flex;flex-direction:column}
  .top{padding:60px 60px 32px}
  .eyebrow{font-size:22px;letter-spacing:7px;text-transform:uppercase;color:${palette.primary};font-weight:600;margin-bottom:18px}
  h1{font-family:${typography.headlineFont};font-size:${titleSize}px;font-weight:${typography.headlineWeight};line-height:1.18;letter-spacing:-1.5px;color:${palette.ink};display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;padding-bottom:.08em}
  h1 .accent{font-style:italic;font-weight:400;color:${palette.accentText ?? palette.ink}}

  .hero{height:380px;background-size:cover;background-position:center;margin:0 60px;border-radius:20px;box-shadow:0 24px 50px rgba(0,0,0,.18)}

  .list{flex:1;padding:24px 60px 24px;display:flex;flex-direction:column;justify-content:center;gap:18px}

  /* text-only rows (fallback) */
  .row{display:flex;align-items:flex-start;gap:24px;list-style:none}
  .num{flex:0 0 72px;width:72px;height:72px;border-radius:50%;background:${palette.primary};color:${numInk};font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:38px;display:flex;align-items:center;justify-content:center;line-height:1;letter-spacing:-1px}
  .row-text{flex:1;min-width:0;padding-top:6px}
  .row-name{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:38px;line-height:1.18;color:${palette.ink};margin-bottom:6px;padding-bottom:.08em}
  .row-cap{font-size:34px;line-height:1.35;color:${palette.ink};opacity:.78;font-weight:400}

  /* thumbnail rows */
  .row-thumbs{flex:1;align-items:center;gap:32px;min-height:0}
  .row-thumbs .thumb{flex:0 0 320px;width:320px;height:280px;background-size:cover;background-position:center;border-radius:20px;box-shadow:0 18px 36px rgba(0,0,0,.18);position:relative;overflow:hidden}
  .row-thumbs .thumb::after{content:"";position:absolute;inset:0;border-radius:20px;box-shadow:inset 0 0 0 1px rgba(0,0,0,.06)}
  .row-thumbs .thumb-num{position:absolute;top:14px;left:14px;width:60px;height:60px;border-radius:50%;background:${palette.primary};color:${numInk};font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:30px;display:flex;align-items:center;justify-content:center;line-height:1;letter-spacing:-1px;z-index:2}
  .row-thumbs .row-text{padding-top:0}
  .row-thumbs .row-name{font-size:44px;margin-bottom:10px}
  .row-thumbs .row-cap{font-size:34px;line-height:1.35}

  /* CTA sits on the LEFT, brand logo on the RIGHT. Pinterest's Lens
     overlay (visual-search magnifying glass) parks in the bottom-right
     corner of the image area on some pins and would otherwise occlude
     the CTA — moving brand to that corner trades brand visibility
     (still readable through Lens) for CTA clickability. */
  .bar{flex:0 0 80px;background:${palette.barBg ?? palette.ink};color:${palette.bg};display:flex;align-items:center;justify-content:space-between;padding:0 60px;font-size:18px;letter-spacing:4px;text-transform:uppercase;font-weight:700}
  .bar .brand-logo{height:34px;width:auto;display:block;filter:${brandLogoFilter}}
  .bar .cta{color:${palette.barCta ?? palette.accent};letter-spacing:2px}
</style></head>
<body><div class="pin">
  <div class="top">
    ${eyebrow ? `<div class="eyebrow">${escapeHtml(eyebrow)}</div>` : ""}
    <h1>${renderTitle(title, titleAccent)}</h1>
  </div>
  ${heroUrl ? `<div class="hero" style="background-image:url('${escapeHtml(heroUrl)}')"></div>` : ""}
  <ul class="list">${rows}</ul>
  <div class="bar"><span class="cta">${escapeHtml(cta ?? "Reveal Venue Names")} →</span>${
    input.hideLogo
      ? ""
      : brandLogo
        ? `<img class="brand-logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
        : `<span>${escapeHtml(brand)}</span>`
  }</div>
</div></body></html>`;
}
